<template>
  <section class="rms-menu max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20" id="menu" aria-labelledby="menu-heading-t1">
    <div class="text-center mb-12 md:mb-16">
      <h2 id="menu-heading-t1" class="text-3xl font-bold tracking-tight mb-2 text-t1-neutral-dark">{{ sectionTitle || $t('public.ourMenu') }}</h2>
      <div class="w-12 h-1 mx-auto rounded" :style="primaryBarStyle"></div>
    </div>
    <p v-if="!displayGroups.length" class="text-t1-neutral-muted text-center py-8">{{ emptyMessage || $t('public.noMenuItems') }}</p>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
      <div v-for="(group, gIdx) in displayGroups" :key="gIdx" class="flex flex-col">
        <div v-if="group.image_url" class="aspect-square w-full max-w-[140px] md:max-w-[180px] rounded-lg overflow-hidden mb-4">
          <img
            :src="group.image_url"
            :alt="group.category_name || 'Category'"
            loading="lazy"
            class="w-full h-full object-cover"
          />
        </div>
        <h3 class="text-sm font-bold uppercase tracking-[0.2em] text-t1-neutral-muted mb-8 border-b border-t1-border pb-2">
          <span :style="getAvailabilityDisplay(group.availability).isUnavailableNow(now) ? { opacity: 0.8 } : undefined">{{ group.category_name }}</span>
          <span v-if="getAvailabilityDisplay(group.availability).label(now)" class="block text-xs font-normal normal-case tracking-normal mt-1">{{ getAvailabilityDisplay(group.availability).label(now) }}</span>
        </h3>
        <p
          v-if="group.category_description"
          class="text-sm text-t1-neutral-muted normal-case tracking-normal font-normal -mt-4 mb-6 max-w-prose"
        >
          {{ group.category_description }}
        </p>
        <div
          class="space-y-8 rms-category-items"
          :class="{ 'rms-category-unavailable': getAvailabilityDisplay(group.availability).isUnavailableNow(now) }"
        >
          <div
            v-for="item in group.items"
            :key="item.uuid"
            class="relative"
          >
            <PublicMenuItemOwnerEditFab
              v-if="showOwnerEditFab"
              :item-uuid="item.catalog_item_uuid || item.uuid"
              :visible="showOwnerEditFab"
            />
            <!-- with_variants: header opens detail; dropdowns update price -->
            <div
              v-if="item.type === 'with_variants'"
              class="rms-menu-item-card rms-menu-item-card--no-click flex flex-col bg-white rounded-xl py-5 px-3 w-full text-left"
            >
              <button
                type="button"
                data-testid="public-menu-item"
                class="rms-menu-item-card rms-menu-item-card--variant-header flex flex-col bg-transparent rounded-xl py-0 px-0 w-full text-left border-none cursor-pointer"
                :class="{ 'rms-item-unavailable': !isItemClickable(item, group) }"
                :aria-disabled="!isItemClickable(item, group)"
                @click="isItemClickable(item, group) ? onVariantHeaderClick(item, group.category_name, $event) : showUnavailableToast(group, item, now)"
              >
                <div class="flex w-full items-center gap-4">
                  <img
                    v-if="itemCardDisplayImageUrl(item)"
                    :src="itemCardDisplayImageUrl(item)"
                    :alt="item.name || 'Untitled'"
                    loading="lazy"
                    class="border w-20 h-20 md:w-24 md:h-24 shrink-0 aspect-square object-cover rounded-lg"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="flex items-end justify-between gap-4 w-full">
                      <div class="flex flex-wrap items-center gap-1.5 min-w-0">
                        <span class="text-lg font-bold text-t1-neutral-dark" :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined">{{ item.name || 'Untitled' }}</span>
                      </div>
                      <div class="menu-dots shrink-0" aria-hidden="true" />
                      <span
                        v-if="item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                        class="text-lg font-bold whitespace-nowrap shrink-0"
                        :style="[primaryTextStyle, itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined]"
                      >
                        {{ formatPrice(resolvedVariantSku(item).price) }}
                      </span>
                      <span v-else-if="item.is_available === false" class="text-sm text-t1-neutral-muted whitespace-nowrap shrink-0">{{ $t('public.notAvailable') }}</span>
                    </div>
                    <p v-if="formatAvailabilityForDisplay(item.availability, now)" class="text-t1-neutral-muted text-xs mt-0.5">{{ formatAvailabilityForDisplay(item.availability, now) }}</p>
                    <p v-if="item.description" class="text-t1-neutral-muted text-sm mt-1">{{ item.description }}</p>
                    <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span
                        v-for="tag in itemTags(item)"
                        :key="tag.uuid"
                        class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                        :style="tagStyle(tag)"
                        :title="tag.text"
                        role="img"
                        :aria-label="tag.text"
                        tabindex="0"
                      >
                        <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                        <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                      </span>
                    </span>
                  </div>
                </div>
              </button>
              <div
                v-if="variantSkus(item).length && variantOptionGroups(item).length"
                class="mt-3 space-y-2 w-full pl-0 sm:pl-[5.5rem] md:pl-[7.5rem]"
                @click.stop
              >
                <div
                  v-for="optGroup in variantOptionGroups(item)"
                  :key="`${item.uuid}-${optGroup.name}`"
                  class="space-y-1"
                >
                  <label
                    class="block text-xs font-semibold uppercase tracking-wider text-t1-neutral-muted"
                    :for="variantSelectId('rms-t1', item, optGroup.name)"
                  >
                    {{ optGroup.name || $t('public.option') }}
                  </label>
                  <select
                    :id="variantSelectId('rms-t1', item, optGroup.name)"
                    class="rms-t1-variant-select w-full min-h-[44px] rounded-lg border border-t1-border bg-white px-3 py-2 text-sm text-t1-neutral-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t1-primary,#1152d4)] focus-visible:ring-offset-2"
                    :value="variantSelectionValue(item, optGroup.name)"
                    :disabled="!isItemClickable(item, group)"
                    :aria-label="`${optGroup.name || $t('public.option')}: ${item.name || ''}`"
                    @change="onVariantGroupChange(item, optGroup.name, $event.target.value)"
                  >
                    <option
                      v-for="val in (optGroup.values || [])"
                      :key="String(val)"
                      :value="val"
                      :disabled="!isVariantGroupValueOrderable(item, optGroup.name, val)"
                    >
                      {{ val }}{{ !isVariantGroupValueOrderable(item, optGroup.name, val) ? ' (' + $t('public.variantOptionNotAvailable') + ')' : '' }}
                    </option>
                  </select>
                </div>
              </div>
              <p v-else-if="variantSkus(item).length && !variantOptionGroups(item).length" class="text-t1-neutral-muted text-sm mt-2 pl-0 sm:pl-[5.5rem]">
                {{ variantOptionGroupsSummary(item) }}
              </p>
            </div>
            <button
              v-else
              type="button"
              data-testid="public-menu-item"
              class="rms-menu-item-card flex flex-col bg-white rounded-xl py-5 px-3 w-full text-left"
              :class="{ 'rms-item-unavailable': !isItemClickable(item, group) }"
              :aria-disabled="!isItemClickable(item, group)"
              @click="isItemClickable(item, group) ? onItemClick(item, group.category_name, $event) : showUnavailableToast(group, item, now)"
            >
              <div class="flex w-full items-center gap-4">
                <img
                  v-if="itemCardDisplayImageUrl(item)"
                  :src="itemCardDisplayImageUrl(item)"
                  :alt="item.name || 'Untitled'"
                  loading="lazy"
                  class="border w-20 h-20 md:w-24 md:h-24 shrink-0 aspect-square object-cover rounded-lg"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-end justify-between gap-4 w-full">
                    <div class="flex flex-wrap items-center gap-1.5 min-w-0">
                      <span class="text-lg font-bold text-t1-neutral-dark" :style="itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined">{{ item.name || 'Untitled' }}</span>
                    </div>
                    <div class="menu-dots shrink-0" aria-hidden="true"></div>
                    <span v-if="item.is_available !== false && itemPrice(item) != null" class="text-lg font-bold whitespace-nowrap shrink-0" :style="[primaryTextStyle, itemUnavailableNow(item.availability) ? { opacity: 0.8 } : undefined]">{{ formatPrice(itemPrice(item)) }}</span>
                    <span v-else-if="item.is_available === false" class="text-sm text-t1-neutral-muted whitespace-nowrap shrink-0">{{ $t('public.notAvailable') }}</span>
                  </div>
                  <p v-if="formatAvailabilityForDisplay(item.availability, now)" class="text-t1-neutral-muted text-xs mt-0.5">{{ formatAvailabilityForDisplay(item.availability, now) }}</p>
                  <p v-if="item.description" class="text-t1-neutral-muted text-sm mt-1">{{ item.description }}</p>
                  <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mt-1.5">
                    <span
                      v-for="tag in itemTags(item)"
                      :key="tag.uuid"
                      class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                      :style="tagStyle(tag)"
                      :title="tag.text"
                      role="img"
                      :aria-label="tag.text"
                      tabindex="0"
                    >
                      <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                      <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                    </span>
                  </span>
                  <ul v-if="item.type === 'combo' && comboEntries(item).length" class="mt-2 list-none pl-4 md:pl-6 space-y-1 text-t1-neutral-muted text-xs border-l-2 border-t1-border ml-0.5" aria-label="Combo contents">
                    <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
                      {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
                    </li>
                  </ul>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { usePublicUnavailableToast } from '@/composables/usePublicUnavailableToast'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { usePublicMenuVariantSelect } from '@/composables/usePublicMenuVariantSelect'
import PublicMenuItemOwnerEditFab from '@/components/public/PublicMenuItemOwnerEditFab.vue'
import { formatAvailabilityForDisplay, isAvailableNow } from '@/utils/availability'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const emit = defineEmits(['select-item'])
const { showUnavailableToast } = usePublicUnavailableToast()

const props = defineProps({
  menuGroups: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  primaryColor: { type: String, default: '' },
  /** Title for the section (e.g. "Our Menu", "Products", "Services") from parent based on business_type. */
  sectionTitle: { type: String, default: '' },
  /** Empty-state message (e.g. "No menu items", "No products", "No services") from parent based on business_type. */
  emptyMessage: { type: String, default: '' },
  /** Logged-in owner on public site: show edit shortcut to catalog item. */
  showOwnerEditFab: { type: Boolean, default: false },
})

function onItemClick(item, categoryName, event) {
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: null,
    trigger: event?.currentTarget ?? null,
  })
}

const {
  displayGroups,
  getAvailabilityDisplay,
  formatPrice,
  itemPrice,
  itemTags,
  comboEntries,
  variantOptionGroupsSummary,
  variantOptionGroups,
  variantSkus,
  itemCardImageUrl,
} = usePublicMenuDisplay(props)

const {
  variantSelectId,
  resolvedVariantSku,
  variantSelectionValue,
  onVariantGroupChange,
  isVariantGroupValueOrderable,
} = usePublicMenuVariantSelect({
  displayGroups,
  variantSkus,
  variantOptionGroups,
})

function onVariantHeaderClick(item, categoryName, event) {
  const sku = resolvedVariantSku(item)
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: sku || null,
    trigger: event?.currentTarget ?? null,
  })
}

function itemCardDisplayImageUrl(item) {
  if (!item) return null
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    return sku?.image_url ?? sku?.imageUrl ?? itemCardImageUrl(item)
  }
  return itemCardImageUrl(item)
}

const now = ref(new Date())
let nowInterval
onMounted(() => {
  nowInterval = setInterval(() => { now.value = new Date() }, 60 * 1000)
})
onUnmounted(() => {
  if (nowInterval) clearInterval(nowInterval)
})

function categoryUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}
function itemUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function isItemClickable(item, group) {
  if (!item || !group) return false
  if (categoryUnavailableNow(group.availability)) return false
  if (item.is_available === false) return false
  if (itemUnavailableNow(item.availability)) return false
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }
  return true
}

function tagStyle(tag) {
  const hex = tag?.color || '#64748b'
  return {
    '--tag-bg': `${hex}20`,
    '--tag-fg': hex,
  }
}

const primaryBarStyle = computed(() => ({ backgroundColor: props.primaryColor || '#1152d4' }))
const primaryTextStyle = computed(() => ({ color: props.primaryColor || '#1152d4' }))
</script>

<style scoped>
.rms-public-tag {
  background-color: var(--tag-bg);
}
.rms-public-tag:hover {
  opacity: 0.9;
}

.rms-menu-item-card:not(.rms-menu-item-card--variant-header) {
  cursor: pointer;
  border: 0.5px solid color-mix(in srgb, var(--t1-primary, #1152d4) 10%, transparent);
  transition: box-shadow 0.2s, transform 0.15s;
  margin-bottom: 1rem;
}

.rms-menu-item-card--no-click {
  cursor: default;
}

.rms-menu-item-card--variant-header:focus-visible {
  outline: 2px solid var(--t1-primary, #1152d4);
  outline-offset: 2px;
}

.rms-t1-variant-select {
  appearance: auto;
}

.rms-menu-item-card:focus-visible {
  outline: 2px solid var(--t1-primary, #1152d4);
  outline-offset: 2px;
}
@media (min-width: 768px) {
  .rms-menu-item-card {
    min-height: 44px;
  }
}

.rms-category-unavailable.rms-category-items {
  filter: grayscale(0.85);
  backdrop-filter: blur(0.5px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  cursor: not-allowed;
  transition: filter 0.2s, opacity 0.2s;
}

.rms-item-unavailable {
  filter: grayscale(0.7) blur(0.5px);
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
